import React from 'react'
import {CircularProgress} from "@mui/material";

import ApiKeyField from "./ApiKeyField";
import SelectTemplateView from "../pages/Contracts/SelectTemplateView";
import {HelpView} from "../pages/Common/HelpView";

export type IntegrationWizardStepType =
    | "info"
    | "api_key_copy"
    | "external_link"
    | "video"
    | "template_select"
    | "import_objects"
    | "custom"

export type IntegrationWizardStep = {
    id: string
    type: IntegrationWizardStepType
    title: string
    description?: string
    list?: string[]
    hint?: string
    imageSrc?: string
    imageAlt?: string
    ctaLabel?: string
    linkUrl?: string
    videoId?: string
    rutubeVideoId?: string
    customKey?: string
}

export type IntegrationWizardImportState = {
    loading: boolean
    success: boolean
    importedCount: number
    apiKeyError: boolean
}

type IntegrationWizardContext = {
    apiKey?: string
    onCopyApiKey?: () => void
    selectedTemplate?: any
    onSelectTemplate?: (template: any) => void
    onNavigateTemplates?: () => void
    importState?: IntegrationWizardImportState
    onImportObjects?: () => void
    onOpenExternalLink?: (url: string) => void
}

type Props = {
    steps: IntegrationWizardStep[]
    currentStepIndex: number
    isStepComplete: (stepId: string) => boolean
    onCompleteStep: (stepId: string) => void
    onChangeStep: (index: number) => void
    onFinish?: () => void
    context?: IntegrationWizardContext
    renderCustomStep?: (step: IntegrationWizardStep) => React.ReactNode
}

function resolveTemplateText(text: string, selectedTemplate?: any) {
    return text.replace(
        "{selectedTemplate}",
        selectedTemplate?.title || "выбранный на первом шаге шаблон"
    )
}

export default function IntegrationWizard({
    steps,
    currentStepIndex,
    isStepComplete,
    onCompleteStep,
    onChangeStep,
    onFinish,
    context,
    renderCustomStep
}: Props) {
    const currentStep = steps[currentStepIndex]
    const totalSteps = steps.length
    const canGoPrev = currentStepIndex > 0
    const canGoNext = currentStepIndex < totalSteps - 1
    const currentStepCompleted = isStepComplete(currentStep.id)

    function goPrev() {
        if (canGoPrev) {
            onChangeStep(currentStepIndex - 1)
        }
    }

    function goNext() {
        if (!currentStepCompleted) {
            if (currentStep.type === "info") {
                onCompleteStep(currentStep.id)
            } else {
                return
            }
        }

        if (canGoNext) {
            onChangeStep(currentStepIndex + 1)
        } else if (onFinish) {
            onFinish()
        }
    }

    function goSkip() {
        if (canGoNext) {
            onChangeStep(currentStepIndex + 1)
        } else if (onFinish) {
            onFinish()
        }
    }

    function renderStepContent(step: IntegrationWizardStep) {
        const description = step.description
            ? resolveTemplateText(step.description, context?.selectedTemplate)
            : null
        const list = step.list
            ? step.list.map((item) => resolveTemplateText(item, context?.selectedTemplate))
            : null

        const image = step.imageSrc ? (
            <div className="integration-wizard__image-wrapper ui-kit-v2">
                <img
                    className="integration-wizard__image"
                    src={step.imageSrc}
                    alt={step.imageAlt || step.title}
                    loading="lazy"
                />
            </div>
        ) : null

        switch (step.type) {
            case "info":
                return (
                    <>
                        {image}
                        {description && (
                            <div className="commonSubtitle ui-kit-v2 integration-wizard__description">
                                {description}
                            </div>
                        )}
                        {list && (
                            <ul className="integration-wizard__list ui-kit-v2">
                                {list.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        )}
                        {step.hint && (
                            <div className="integration-wizard__hint ui-kit-v2">
                                {step.hint}
                            </div>
                        )}
                    </>
                )
            case "api_key_copy":
                return (
                    <ApiKeyField
                        title={step.description || "Ваш API ключ:"}
                        value={context?.apiKey || ""}
                        buttonClassName="action-button primary ui-kit-v2 integration-wizard__action-button"
                        buttonTextClassName="button-text"
                        onCopy={() => {
                            if (context?.onCopyApiKey) {
                                context.onCopyApiKey()
                            }
                            onCompleteStep(step.id)
                        }}
                    />
                )
            case "external_link":
                return (
                    <>
                        {image}
                        {description && (
                            <div className="commonSubtitle ui-kit-v2 integration-wizard__description integration-wizard__description--wide">
                                {description}
                            </div>
                        )}
                        {list && (
                            <ul className="integration-wizard__list integration-wizard__list--wide ui-kit-v2">
                                {list.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        )}
                        <button
                            className="action-button primary ui-kit-v2 integration-wizard__action-button"
                            onClick={() => {
                                const url = step.linkUrl || ""
                                if (context?.onOpenExternalLink) {
                                    context.onOpenExternalLink(url)
                                } else {
                                    const newTab = window.open(url, "_blank")
                                    if (newTab) {
                                        newTab.opener = null
                                    }
                                }
                                onCompleteStep(step.id)
                            }}
                        >
                            <span className="button-text">{step.ctaLabel || "Открыть"}</span>
                        </button>
                    </>
                )
            case "video":
                return (
                    <>
                        {image}
                        <HelpView
                            title={step.title}
                            subtitle={description || ""}
                            videoId={step.videoId || ""}
                            rutubeVideoId={step.rutubeVideoId || ""}
                        />
                    </>
                )
            case "template_select":
                return (
                    <>
                        {image}
                        {description && (
                            <div className="commonSubtitle ui-kit-v2 integration-wizard__description">
                                {description}
                            </div>
                        )}
                        {list && (
                            <ul className="integration-wizard__list ui-kit-v2">
                                {list.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        )}
                        <div className="commonLabel ui-kit-v2 integration-wizard__section-title">
                            Выберите шаблон:
                        </div>
                        <SelectTemplateView
                            selectedTemplate={context?.selectedTemplate}
                            didSelectTemplate={(template: any) => {
                                if (context?.onSelectTemplate) {
                                    context.onSelectTemplate(template)
                                }
                                onCompleteStep(step.id)
                            }}
                        />
                        {step.ctaLabel && context?.onNavigateTemplates && (
                            <button
                                className="action-button secondary ui-kit-v2 integration-wizard__action-button integration-wizard__action-button--templates"
                                onClick={() => context.onNavigateTemplates && context.onNavigateTemplates()}
                            >
                                <span className="button-text">{step.ctaLabel}</span>
                            </button>
                        )}
                    </>
                )
            case "import_objects":
                return (
                    <>
                        {image}
                        {description && (
                            <div className="commonSubtitle ui-kit-v2 integration-wizard__description">
                                {description}
                            </div>
                        )}
                        {list && (
                            <ul className="integration-wizard__list ui-kit-v2">
                                {list.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        )}
                        {step.hint && (
                            <div className="integration-wizard__warning ui-kit-v2">
                                {step.hint}
                            </div>
                        )}
                        {!context?.importState?.success && !context?.importState?.apiKeyError ? (
                            <button
                                className="action-button primary ui-kit-v2 integration-wizard__action-button"
                                onClick={() => context?.onImportObjects && context.onImportObjects()}
                                disabled={context?.importState?.loading}
                            >
                                {context?.importState?.loading && (
                                    <CircularProgress size={"20px"} sx={{color: "#1FA3EE"}} />
                                )}
                                <span className="button-text">{step.ctaLabel || "Импортировать объекты"}</span>
                            </button>
                        ) : context?.importState?.apiKeyError ? (
                            <div className="integration-wizard__error ui-kit-v2">
                                <div className="integration-wizard__error-title ui-kit-v2">
                                    🔑 API ключ не настроен
                                </div>
                                <div className="integration-wizard__error-subtitle ui-kit-v2">
                                    API ключ RealtyCalendar не настроен или неверный
                                </div>
                                <div className="integration-wizard__error-note ui-kit-v2">
                                    Перейдите в личный кабинет RealtyCalendar и настройте интеграцию с OkiDoki
                                </div>
                            </div>
                        ) : (
                            <div className="integration-wizard__success ui-kit-v2">
                                <div className="integration-wizard__success-title ui-kit-v2">
                                    ✅ Импорт завершен успешно!
                                </div>
                                <div className="integration-wizard__success-subtitle ui-kit-v2">
                                    Импортировано {context?.importState?.importedCount || 0} объектов
                                </div>
                            </div>
                        )}
                    </>
                )
            case "custom":
                return renderCustomStep ? renderCustomStep(step) : null
            default:
                return null
        }
    }

    return (
        <div className="integration-wizard ui-kit-v2">
            <div className="commonLabel ui-kit-v2 integration-wizard__title">
                {currentStep.title}
            </div>
            {renderStepContent(currentStep)}

            <div className="integration-wizard__footer ui-kit-v2">
                <button
                    className="action-button secondary ui-kit-v2 integration-wizard__footer-button"
                    onClick={goPrev}
                    disabled={!canGoPrev}
                >
                    <span className="button-text">Назад</span>
                </button>
                <div className="integration-wizard__step-count ui-kit-v2">
                    Шаг {currentStepIndex + 1} из {totalSteps}
                </div>
                <div className="integration-wizard__footer-actions ui-kit-v2">
                    {canGoNext && (
                        <button
                            className="action-button secondary ui-kit-v2 integration-wizard__footer-button"
                            onClick={goSkip}
                        >
                            <span className="button-text">Пропустить</span>
                        </button>
                    )}
                    <button
                        className="action-button primary ui-kit-v2 integration-wizard__footer-button"
                        onClick={goNext}
                        disabled={(!currentStepCompleted && currentStep.type !== "info") || (!canGoNext && !onFinish)}
                    >
                        <span className="button-text">{canGoNext ? "Далее" : "Готово"}</span>
                    </button>
                </div>
            </div>
        </div>
    )
}
